/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';

@Injectable()
export class ColouriseService {
    /**
     * Convert data to string.
     * @param data
     */
    public convertDataToString(data: any): string {
        const stringified: string = JSON.stringify(data);
        const formattedString: any[] = [];
        const indentIncrementer: any[] = [];
        for (const character of stringified) {
            if (character === '}' || character === ']') {
                formattedString.push('\n');
                indentIncrementer.pop();
                this.checkIndentIncrementer(indentIncrementer, formattedString);
            }

            formattedString.push(character);

            if (character === '{' || character === '[') {
                formattedString.push('\n');
                indentIncrementer.push(1);
                this.checkIndentIncrementer(indentIncrementer, formattedString);
            }

            if (character === ',') {
                formattedString.push('\n');
                this.checkIndentIncrementer(indentIncrementer, formattedString);
            }

            if (character === ':') {
                formattedString.push(' ');
            }
        }

        return formattedString.join('');
    }

    /**
     * Check indent incrementer.
     * @param indentIncrementer
     * @param formattedString
     * @private
     */
    private checkIndentIncrementer(indentIncrementer: any[], formattedString: any[]) {
        if (indentIncrementer?.length) {
            indentIncrementer.forEach(() => formattedString.push('    '));
        }
    }

    /**
     * Display the data from response in the view
     * @param response
     */
    public displayDataInView(response: any): any {
        let colourFormatted = null;
        switch (response['type']) {
            case 'file':
                colourFormatted = this.colourCodeFile(response['data']);
                break;
            case 'data':
                colourFormatted = this.colorCodeData(response);
                break;
            case 'format':
                colourFormatted = this.colorCodeFormat(response);
                break;
            default:
                return null;
        }

        return colourFormatted;
    }

    /**
     * Colour code response of type format.
     * @param response
     * @private
     */
    private colorCodeFormat(response: any): string {
        let output: string;
        output = '';
        for (const key in response['data']) {
            if (response['data'].hasOwnProperty(key)) {
                // Break out the headings
                output += '<br><code><strong class="key">' + key + '</strong></code><br>';
                output += '<code>' + response['data'][key].join('<br>') + '</code><br>';
            }
        }

        return output;
    }

    /**
     * Colour code response of type data.
     * @param response
     * @private
     */
    private colorCodeData(response: any): string {
        const newLines: any[] = [];
        for (const line of response['data']) {
            if (/^([0-9]+):/.test(line)) {
                newLines.push(`<span class="number">${line}</span>`);
            } else {
                newLines.push(line);
            }
        }

        return newLines.join('\n');
    }

    /**
     * Colour code response of type file.
     * @param data
     * @private
     */
    private colourCodeFile(data: any): string {
        if (data.startsWith('{')) {
            return this.colorCodeJSON(data.split(' '));
        } else if (data.startsWith('#Haproxy')) {
            const groups = data.split('\n');

            return this.colorCodeHaConfig(groups);
        } else if (data.startsWith('# snmp')) {
            const lines = data.split('\n');

            return this.colorCodeSnmpConfig(lines);

        } else {
            return data;
        }
    }

    /**
     * Colour code response of type HA Config.
     * @param groups
     * @private
     */
    private colorCodeHaConfig(groups: any): string {
        const newGroups: any[] = [];
        for (const group of groups) {
            if (/^[A-Z0-9]/i.test(group)) {
                newGroups.push(`<span class="key">${group}</span>\n`);
            } else {
                newGroups.push(group + '\n');
            }
        }

        return newGroups.join(' ');
    }

    /**
     * Colour code response of type SNMP Config.
     * @param lines
     * @private
     */
    private colorCodeSnmpConfig(lines: any): string {
        const newLines: any[] = [];
        for (const line of lines) {
            if (/^[A-Z0-9]/i.test(line)) {
                newLines.push(line + '\n');
            } else {
                newLines.push(`<span class="key">${line}</span>\n`);
            }
        }

        return newLines.join('');
    }

    /**
     * Colour code response of type JSON.
     * @param groups
     * @private
     */
    private colorCodeJSON(groups: any): any {
        const key: RegExp = new RegExp('((?:\"|[^"])*")(:)');
        const value: RegExp = new RegExp('((?:"|[^"])*")(,)|((?:"|[^"])*")');
        const newGroups: any[] = [];
        let index: number = 0;
        let timeZoneIndex: number = 0;
        for (const group of groups) {
            if (index !== timeZoneIndex + 1 && index !== timeZoneIndex + 2) {
                if (group === '"timeZone":') {
                    timeZoneIndex = index;
                    newGroups.push(this.formatTimeZoneGroup(groups, group, index));
                } else if (key.test(group)) {
                    const matches = group.match(key);
                    newGroups.push(`<span class="key">${matches[1]}</span><span class="colon">${matches[2]}</span>`);
                } else if (value.test(group)) {
                    newGroups.push(this.formatValue(group, value));
                } else if (group.includes('{') || group.includes('}') || group.includes('[') || group.includes(']')) {
                    newGroups.push(`<span class="curly-bracket">${group}</span>`);
                } else {
                    newGroups.push(this.formatOtherValues(group));
                }
            }

            index++;
        }

        return newGroups.join(' ');
    }

    /**
     * Format time zone group.
     * @param groups
     * @param group
     * @param index
     * @private
     */
    private formatTimeZoneGroup(groups: any, group: string, index: number): string {
        const key: string[] = group.split(':');
        const timeZonePart1: string = this.cleanTimeZoneParts(groups, index + 1);
        let timeZonePart2: string = this.cleanTimeZoneParts(groups, index + 2);
        if (timeZonePart2) {
            timeZonePart2 = ' ' + timeZonePart2;
        }

        // eslint-disable-next-line max-len
        return `<span class="key">${key[0]}</span><span class="colon">: </span><span class="value">${timeZonePart1}${timeZonePart2}</span><span class="comma">,</span>\n`;
    }

    /**
     * Clean the time zone parts
     * @param groups
     * @param index
     * @private
     */
    private cleanTimeZoneParts(groups: any, index: number): string {
        return groups[index].replace(',', '').replace('\n', '');
    }

    /**
     * Format value.
     * @param group
     * @param value
     * @private
     */
    private formatValue(group: string, value: RegExp): string {
        let newValue: string;
        const excludeLineBreak: boolean = /[a-zA-Z]+$/.test(group);
        const matches: RegExpMatchArray = group.match(value);

        if (matches[1] && matches[2]) {
            newValue = `<span class="value">${matches[1]}</span><span class="comma">${matches[2]}</span>\n`;
        }

        if (matches[3]) {
            newValue = `<span class="value">${excludeLineBreak ? matches.input : matches[3]}</span>${excludeLineBreak ? '' : '\n'}`;
        }

        return newValue;
    }

    /**
     * Format other values not caught by key or value match.
     * @param group
     * @private
     */
    private formatOtherValues(group: string): string {
        let newValue: string;

        const newString: string = group.replace(',', '').trim();
        if (newString === 'true' || newString === 'false' || newString === 'null') {
            newValue = group.includes(',')
                ? `<span class="boolean">${newString}</span><span class="comma">,</span>\n`
                : `<span class="boolean">${newString}</span>\n`;
        } else if (/^\d+$/.test(newString)) {
            newValue = group.includes(',')
                ? `<span class="number">${newString}</span><span class="comma">,</span>\n`
                : `<span class="number">${newString}</span>\n`;
        } else {
            newValue = `<span class="value">${group}</span>`;
        }

        return newValue;
    }
}
